import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFirestoreCollection } from "../firebase/listeners.js";
import type { EditableScene } from "../lib/scene-edits.js";
import AspectCard from "./AspectCard.js";
import SceneEditor from "./SceneEditor.js";

export default function SceneBoard({
  tableId,
  isGM,
}: {
  tableId: string;
  isGM: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { data, loading } = useFirestoreCollection<EditableScene>(`tables/${tableId}/scenes`);
  const [editing, setEditing] = useState(false);

  if (loading) return <p className="text-sm text-muted-foreground">{t("app.loading")}</p>;
  const active = data.find((s) => s.status === "active");

  if (!active) return <p className="text-sm text-muted-foreground">{t("scene.none")}</p>;

  const scene = active;
  const sceneId = active.id;
  const scenePath = `/tables/${tableId}/scenes/${sceneId}`;
  const aspects = [...(scene.sceneAspects ?? []), ...(scene.situationalAspects ?? [])];

  if (editing && isGM) {
    return (
      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{scene.name}</h2>
          <button
            type="button"
            className="rounded border px-2 py-1 text-xs"
            onClick={() => setEditing(false)}
          >
            {t("edit.exit")}
          </button>
        </div>
        <SceneEditor scenePath={scenePath} scene={scene} />
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{scene.name}</h2>
        {isGM && (
          <button
            type="button"
            className="rounded border px-2 py-1 text-xs"
            onClick={() => setEditing(true)}
          >
            {t("edit.enter")}
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {aspects.map((a) => (
          <AspectCard key={a.id} aspect={a} isGM={isGM} />
        ))}
      </div>
    </section>
  );
}
